import { DiContainer } from '@jkhy/frontend-utils/di';
import { InjectedWindow } from '@treasury/core/config/configuration.types';
import { COMPANY_USER_TAG_NAME } from '@treasury/domain-components/company-user/company-user.component.data';
import {
    COMPANY_USER_LIST_TAG_NAME,
    COMPANY_USERS_TAG_NAME,
} from '@treasury/domain-components/company-users/company-users.types';
import { BillingRoute } from '@treasury/domain/backoffice/services/billing/billing.types';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags/feature-flag.service';
import { Feature } from '@treasury/domain/services/feature-flags/feature-flag.types';
import { Commands, Context, Route } from '@vaadin/router';
import { ASSOCIATED_COMPANIES_CARD_TAG_NAME } from './associated-companies/associated-company.types';
import {
    AUTHENTICATION_PROFILE_DETAIL_TAG_NAME,
    AUTHENTICATION_PROFILE_LIST_TAG_NAME,
} from './authentication-profile/authentication-profile.data';
import { isAuthorized } from './authGuard';
import {
    ACCOUNT_DETAIL_TAG_NAME,
    ACCOUNTS_CARD_TAG_NAME,
    ACCOUNTS_TABLE_TAG_NAME,
} from './company-accounts/accounts.data';
import {
    BILLING_ACCOUNT_RECON_TAG_NAME,
    BILLING_ACH_CONTAINER_TAG_NAME,
    BILLING_DETAILS_TAG_NAME,
    BILLING_POS_PAY_CONTAINER_TAG_NAME,
    BILLING_STP_PAY_CONTAINER_TAG_NAME,
    BILLING_TRANSFER_CONTAINER_TAG_NAME,
    BILLING_WIRES_TAG_NAME,
} from './company-billing/company-billing.data';
import {
    COMPANY_INFORMATION_TAG_NAME,
    SUMMARY_TAG_NAME,
} from './company-information/company-information.data';
import {
    ACCOUNT_RECON_TAG_NAME,
    ACH_COMPANY_DETAIL_TAG_NAME,
    ACH_PRODUCT_FEATURE_WRAPPER_TAG_NAME,
    AGILETICS_WEB_ESCROW_TAG_NAME,
    AMPLIFI_LOYALTY_TAG_NAME,
    BILL_PAY_TAG_NAME,
    CENTRIX_POSITIVE_PAY_SSO_TAG_NAME,
    COMPANY_PRODUCT_LIST_TAG_NAME,
    COMPANY_PRODUCTS_TAG_NAME,
    ESI_TAG_NAME,
    FOREIGN_CURRENCY_WIRE_TAG_NAME,
    POSITIVE_PAY_TAG_NAME,
    REMOTE_TAG_NAME,
    REPORTING_TAG_NAME,
    STOP_PAYMENT_TAG_NAME,
    TO_ACH_COMPANY_DETAIL_WRAPPER_TAG_NAME,
    TRANSFER_TAG_NAME,
    WAUSAU_E_STATEMENT_TAG_NAME,
    WIRE_COMPANY_ACCOUNTS_TAG_NAME,
    WIRE_TAG_NAME,
    WIRE_WRAPPER_TAG_NAME,
} from './company-products/company-products.data';
import { PrettyFeatureType } from './company-products/company-products.types';
import { COMPANY_LAYOUT_TAG_NAME, LAYOUT_TAG_NAME } from './components/layouts/layouts.data';
import { CONTENT_MANAGEMENT_CONFIGURATION_CONTAINER_TAG_NAME } from './content-management-configuration/content-management-configuration.data';
import { DASHBOARD_TAG_NAME } from './dashboard/dashboard.data';
import { TM_BO_FORGOT_PASSWORD_TAG_NAME } from './forgot-password/container';
import { LOGIN_TAG_NAME } from './login/login.data';
import { XPERIENCE_LOGIN_TAG_NAME } from './login/xperience-login/xperience-login.data';
import { MESSAGE_CENTER_CONFIGURATION_TAG_NAME } from './message-center-configuration/message-center-configuration.data';
import { MESSAGE_CENTER_CONTAINER_TAG_NAME } from './message-center/message-center.data';
import { NEW_USER_REGISTRATION_TAG_NAME } from './new-user-registration/new-user-registration.data';
import { NOTIFICATION_CONFIGURATION_TAG_NAME } from './notification-configuration/notification-configuration.data';
import {
    ACH_FILTER_EXCEPTION_TAG_NAME,
    ACH_PAYMENT_DETAIL_TAG_NAME,
    CUSTOMER_USER_ACTIVITY_TAG_NAME,
    FI_USER_ACTIVITY_TAG_NAME,
    REPORT_DASHBOARD_TAG_NAME,
    REPORT_VIEW_TAG_NAME,
    WIRE_PAYMENT_DETAIL_TAG_NAME,
    WIRE_UPLOAD_TAG_NAME,
} from './reports/reports.data';
import { RESET_PASSWORD_TAG_NAME } from './reset-password/reset-password.data';
import { TERMS_CONDITIONS_CONFIGURATION_TAG_NAME } from './terms-conditions-configuration/terms-conditions-configuration.data';

import { HELP_CONTAINER_TAG_NAME } from './help/help.data';
import {
    COMPANY_SEARCH_TAG_NAME,
    INSTITUTION_SEARCH_TAG_NAME,
    USER_SEARCH_TAG_NAME,
} from './search/search.data';
import {
    FI_USER_LIST_TAG_NAME,
    FI_USER_PROFILE_TAG_NAME,
    FI_USER_ROLE_TAG_NAME,
    FI_USER_ROLES_TAG_NAME,
    FI_USER_ROLES_WRAPPER_TAG_NAME,
    FI_USERS_WRAPPER_TAG_NAME,
} from './users/fi-users/fi-users.data';

export enum RoutePath {
    App = 'treasury',
    Root = '/',
    Login = '/login',
    XperienceLogin = '/xperience/login',
    ForgotPassword = '/forgot-password',
    NewUserRegistration = '/user-verification',
    FiUsers = '/fi-users',
    FiUserProfile = '/:fiUserId',
    FiUserRoles = '/roles',
    Company = '/company/:companyId',
    Dashboard = '/dashboard',
    CompanySearch = '/company-search',
    InstitutionSearch = '/institution-search',
    UserSearch = '/user-search',
    AssociatedCompanies = '/associated-companies',
    Messages = '/messages',
    Reports = '/reports',
    FiProductConfiguration = '/fi/product-configuration',
    FiCompanyUserSettings = '/fi/company-user-settings',
    FiUserSettings = '/fi/fi-user-settings',
    CustomerEngagement = '/fi/customer-engagement',
    ProcessingConfiguration = '/fi/processing-configuration',
    TransactionCodeConfiguration = '/tc-configuration',
    TermsConditionsConfiguration = '/terms-conditions-configuration',
    AuthenticationProfileConfiguration = '/authentication-profile',
    NotificationConfiguration = '/notification-configuration',
    MessageCenterConfiguration = '/message-center-configuration',
    ContentManagementConfiguration = '/fi/content-management-configuration',
    FiBillingConfiguration = '/fi/billing-configuration',
    UserVerificationReset = '/user-verification-reset',
    Help = '/help',
}

const getEntitlementsService = async (): Promise<EntitlementsService> =>
    (await DiContainer.getInstance()).getAsync(EntitlementsService);

const handleRootPathRedirect = async (
    context: Context,
    commands: Commands,
    defaultRoute: string
) => {
    const { config } = window as unknown as InjectedWindow;
    const basePath = config?.basePath;
    if (!basePath) {
        return undefined;
    }

    const guidPattern = /^[0-9a-fA-F-]{36}$/;
    const normalizePath = (path: string): string => path.replace(/\/$/, '');

    const basePathNormalized = normalizePath(basePath);
    const currentPathNormalized = normalizePath(context.pathname);

    // Don't redirect if we're already at or past the default route
    if (currentPathNormalized.includes(defaultRoute.replace(/\/$/, ''))) {
        return undefined;
    }

    // Check if we're at the root base path
    if (currentPathNormalized === basePathNormalized) {
        return commands.redirect(defaultRoute);
    }

    // Check if we're at basePath + institutionId (GUID)
    const pathAfterBase = currentPathNormalized.slice(basePathNormalized.length + 1);
    if (
        pathAfterBase &&
        guidPattern.test(pathAfterBase) &&
        currentPathNormalized === `${basePathNormalized}/${pathAfterBase}`
    ) {
        const finalRoute = `/${pathAfterBase}${defaultRoute}`;
        return commands.redirect(finalRoute);
    }

    return undefined;
};

const guardCompanyProfile = async (context: Context, commands: Commands) => {
    const svc = await getEntitlementsService();
    const [canAccess, canSwitchFi] = await Promise.all([
        svc.isEnabled(Entitlements.AccessCompanyProfile),
        svc.isEnabled(Entitlements.SwitchFi),
    ]);
    if (!canAccess && !canSwitchFi) {
        const institutionId = context.params.institutionId as string | undefined;
        const prefix = institutionId ? `/${institutionId}` : '';
        return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
    }
    return undefined;
};

const routeRouteChildRoutes: Route[] = [
    {
        path: '/company/:companyId/users/create',
        component: COMPANY_USER_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            const canAdd = await (
                await getEntitlementsService()
            ).isEnabled(Entitlements.AddCompanyUser);
            if (!canAdd) {
                const companyId = context.params.companyId as string | undefined;
                return commands.redirect(`/company/${companyId}/users`);
            }
            await import('@treasury/domain-components/company-user/company-user.component');
            return undefined;
        },
    },
    {
        path: '/company/:companyId/users/:userId(\\d+)/:tab/:product/:subProduct',
        component: COMPANY_USER_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            await import('@treasury/domain-components/company-user/company-user.component');
            return undefined;
        },
    },
    {
        path: '/company/:companyId/users/:userId(\\d+)/:tab/:product',
        component: COMPANY_USER_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            await import('@treasury/domain-components/company-user/company-user.component');
            return undefined;
        },
    },
    {
        path: '/company/:companyId/users/:userId(\\d+)/:tab',
        component: COMPANY_USER_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            await import('@treasury/domain-components/company-user/company-user.component');
            return undefined;
        },
    },
    {
        path: '/company/:companyId/users/:userId(\\d+)',
        component: COMPANY_USER_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            await import('@treasury/domain-components/company-user/company-user.component');
            return undefined;
        },
    },
    {
        path: RoutePath.Messages,
        component: MESSAGE_CENTER_CONTAINER_TAG_NAME,
        action: async () => {
            await import('./message-center/container');
        },
    },
    {
        path: '/messages/:messageId',
        component: MESSAGE_CENTER_CONTAINER_TAG_NAME,
        action: async () => {
            await import('./message-center/container');
            await import('./message-center/overlays/drawers/message-detail');
        },
    },
    {
        path: RoutePath.Company,
        component: COMPANY_LAYOUT_TAG_NAME,
        action: async (context, commands) => {
            const profileRedirect = await guardCompanyProfile(context, commands);
            if (profileRedirect) {
                return profileRedirect;
            }
            await import('./components/layouts/company-layout/tm-bo-company-layout.component');
            return undefined;
        },
        children: [
            {
                path: RoutePath.AssociatedCompanies,
                component: ASSOCIATED_COMPANIES_CARD_TAG_NAME,
                action: async () => {
                    await import('./associated-companies/associated-companies-card');
                },
            },
            {
                path: 'accounts',
                component: ACCOUNTS_CARD_TAG_NAME,
                action: async () => {
                    await import('./company-accounts/accounts.component');
                },
                children: [
                    {
                        path: '/:accountId',
                        component: ACCOUNT_DETAIL_TAG_NAME,
                        action: async () => {
                            await import('./company-accounts/partials/account-detail');
                        },
                    },
                    {
                        path: '(.*)',
                        component: ACCOUNTS_TABLE_TAG_NAME,
                        action: async () => {
                            await import('./company-accounts/partials/accounts-table');
                        },
                    },
                ],
            },
            {
                path: 'products',
                component: COMPANY_PRODUCTS_TAG_NAME,
                animate: true,
                action: async () => {
                    await import('./company-products/company-products.component');
                },
                children: [
                    {
                        path: `${PrettyFeatureType.AccountRecon}`,
                        component: ACCOUNT_RECON_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/account-recon-reporting/account-recon-reporting-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.AmpliFiLoyalty}`,
                        component: AMPLIFI_LOYALTY_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/amplifi-loyalty/amplifi-loyalty-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.CentrixSso}`,
                        component: CENTRIX_POSITIVE_PAY_SSO_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/centrix-positive-pay-sso/centrix-positive-pay-sso-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.Wire}`,
                        component: WIRE_WRAPPER_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/wire/wire-detail-wrapper.component');
                        },
                        children: [
                            {
                                path: ':coreCompanyId/:mode',
                                component: WIRE_COMPANY_ACCOUNTS_TAG_NAME,
                                action: async () => {
                                    await import('./company-products/partials/products/wire/wire-company-accounts.partial');
                                },
                            },
                            {
                                path: '',
                                component: WIRE_TAG_NAME,
                                action: async () => {
                                    await import('./company-products/partials/products/wire/wire-detail.component');
                                },
                            },
                        ],
                    },
                    {
                        path: `${PrettyFeatureType.ACH}`,
                        component: ACH_PRODUCT_FEATURE_WRAPPER_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/ach/ach-product-feature-wrapper.component');
                        },
                        children: [
                            {
                                path: ':achCompanyId',
                                component: TO_ACH_COMPANY_DETAIL_WRAPPER_TAG_NAME,
                                action: async () => {
                                    await import('./company-products/partials/products/ach/to-ach-company/to-ach-company-detail-wrapper.component');
                                },
                            },
                            {
                                path: '(.*)',
                                component: ACH_COMPANY_DETAIL_TAG_NAME,
                                action: async () => {
                                    await import('./company-products/partials/products/ach/ach-company/ach-company-detail.component');
                                },
                            },
                        ],
                    },
                    {
                        path: `${PrettyFeatureType.PositivePay}`,
                        component: POSITIVE_PAY_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/positive-pay/positive-pay-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.StopPayment}`,
                        component: STOP_PAYMENT_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/stop-payment/stop-payment-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.Reporting}`,
                        component: REPORTING_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/reporting/reporting-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.BillPay}`,
                        component: BILL_PAY_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/bill-pay/bill-pay-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.WausauEStatement}`,
                        component: WAUSAU_E_STATEMENT_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/wausau-e-statements/wausau-e-statements-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.AgileticsWebEscrow}`,
                        component: AGILETICS_WEB_ESCROW_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/agiletics-web-escrow/agiletics-web-escrow-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.ForeignCurrencyWire}`,
                        component: FOREIGN_CURRENCY_WIRE_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/foreign-currency-wire/foreign-currency-wire-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.ElectronicStatementInteractive}`,
                        component: ESI_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/electronic-statement-interactive/electronic-statement-interactive-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.Transfer}`,
                        component: TRANSFER_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/internal-transfer/internal-transfer-detail.component');
                        },
                    },
                    {
                        path: `${PrettyFeatureType.RemoteDeposit}`,
                        component: REMOTE_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/products/remote-deposit-capture/remote-deposit-capture-detail.component');
                        },
                    },
                    {
                        path: '(.*)',
                        component: COMPANY_PRODUCT_LIST_TAG_NAME,
                        action: async () => {
                            await import('./company-products/partials/product-list/product-list.component');
                        },
                    },
                ],
            },
            {
                path: 'users',
                component: COMPANY_USERS_TAG_NAME,
                action: async () => {
                    await import('@treasury/domain-components/company-users/company-users.component');
                },
                children: [
                    {
                        path: 'create',
                        component: COMPANY_USERS_TAG_NAME,
                        action: async (context, commands) => {
                            const canAdd = await (
                                await getEntitlementsService()
                            ).isEnabled(Entitlements.AddCompanyUser);
                            if (!canAdd) {
                                const companyId = context.params.companyId as string | undefined;
                                return commands.redirect(`/company/${companyId}/users`);
                            }
                            await import('@treasury/domain-components/company-user/company-user.component');
                            return undefined;
                        },
                    },
                    {
                        path: ':userId(\\d+)',
                        component: COMPANY_USERS_TAG_NAME,
                        action: async () => {
                            await import('@treasury/domain-components/company-user/company-user.component');
                        },
                    },
                    {
                        path: '',
                        component: COMPANY_USER_LIST_TAG_NAME,
                        action: async () => {
                            await import('@treasury/domain-components/company-users/partials/company-user-list/company-user-list.component');
                        },
                    },
                ],
            },
            {
                path: 'billing',
                children: [
                    {
                        path: BillingRoute.ACH,
                        component: BILLING_ACH_CONTAINER_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/ach/container');
                        },
                    },
                    {
                        path: BillingRoute.ACCOUNT_RECONCILIATION,
                        component: BILLING_ACCOUNT_RECON_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/account-recon/container');
                        },
                    },

                    {
                        path: BillingRoute.POSITIVE_PAY,
                        component: BILLING_POS_PAY_CONTAINER_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/pos-pay/container');
                        },
                    },

                    {
                        path: BillingRoute.STOP_PAYMENT,
                        component: BILLING_STP_PAY_CONTAINER_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/stp-pay/container');
                        },
                    },

                    {
                        path: BillingRoute.TRANSFER,
                        component: BILLING_TRANSFER_CONTAINER_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/transfer/container');
                        },
                    },
                    {
                        path: BillingRoute.WIRE,
                        component: BILLING_WIRES_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/wire/container');
                        },
                    },
                    {
                        path: `:type/:transactionId`,
                        component: BILLING_DETAILS_TAG_NAME,
                        action: async () => {
                            await import('./company-billing/generic-components/billing-details.component');
                        },
                    },
                ],
            },
            {
                path: '',
                component: COMPANY_INFORMATION_TAG_NAME,
                action: async () => {
                    await import('./company-information/company-information.component');
                },
                children: [
                    {
                        path: '(.*)',
                        component: SUMMARY_TAG_NAME,
                        action: async () => {
                            await import('./company-information/partials/summary/summary.component');
                        },
                    },
                ],
            },
        ],
    },
    {
        path: RoutePath.Dashboard,
        component: DASHBOARD_TAG_NAME,
        action: async () => {
            await import('./dashboard/container');
        },
    },
    {
        path: RoutePath.Reports,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const redirectToDashboard = () => commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            try {
                const di = await DiContainer.getInstance();
                const ffService = await di.getAsync(FeatureFlagService);
                const reportsEnabled = await ffService.isEnabled(Feature.BoV2Reports);
                if (!reportsEnabled) {
                    return redirectToDashboard();
                }
                const hasReportsEntitlement = await (
                    await getEntitlementsService()
                ).isEnabled(Entitlements.ManageReports);
                if (!hasReportsEntitlement) {
                    return redirectToDashboard();
                }
            } catch {
                return redirectToDashboard();
            }

            return undefined;
        },
        children: [
            {
                path: '',
                component: REPORT_DASHBOARD_TAG_NAME,
                action: async () => {
                    await import('./reports/dashboard/report-dashboard.component');
                },
            },
            {
                path: '/ach-payment-detail/:paymentTypeId/:paymentId',
                component: ACH_PAYMENT_DETAIL_TAG_NAME,
                action: async () => {
                    await import('./reports/ach-payments/detail/ach-payment-detail.component');
                },
            },
            {
                path: '/wire-payment-detail/:wireId',
                component: WIRE_PAYMENT_DETAIL_TAG_NAME,
                action: async () => {
                    await import('./reports/wire-transfers/detail/wire-payment-detail.component');
                },
            },
            {
                path: '/ach-filter-exception/:reportId',
                component: ACH_FILTER_EXCEPTION_TAG_NAME,
                action: async () => {
                    await import('./reports/ach-filter-exception/ach-filter-exception.component');
                },
            },
            {
                path: '/wire-upload/:reportId',
                component: WIRE_UPLOAD_TAG_NAME,
                action: async () => {
                    await import('./reports/wire-upload/wire-upload.component');
                },
            },
            {
                path: '/fi-user-activity/:reportId',
                component: FI_USER_ACTIVITY_TAG_NAME,
                action: async () => {
                    await import('./reports/user-activity/fi-user-activity.component');
                },
            },
            {
                path: '/customer-user-activity/:reportId',
                component: CUSTOMER_USER_ACTIVITY_TAG_NAME,
                action: async () => {
                    await import('./reports/user-activity/customer-user-activity.component');
                },
            },
            {
                path: '/:reportType/:reportId',
                component: REPORT_VIEW_TAG_NAME,
                action: async () => {
                    await import('./reports/shared/report-view.component');
                },
            },
        ],
    },
    {
        path: RoutePath.CompanySearch,
        component: COMPANY_SEARCH_TAG_NAME,
        action: async (context, commands) => {
            const svc = await getEntitlementsService();
            const [canSearch, canSwitchFi] = await Promise.all([
                svc.isEnabled(Entitlements.ExecuteCompanySearch),
                svc.isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canSearch && !canSwitchFi) {
                const institutionId = context.params.institutionId as string | undefined;
                const prefix = institutionId ? `/${institutionId}` : '';
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./search/company/container');
            return undefined;
        },
    },
    {
        path: RoutePath.InstitutionSearch,
        component: INSTITUTION_SEARCH_TAG_NAME,
        action: async () => {
            await import('./search/institution/container');
        },
    },
    {
        path: RoutePath.UserSearch,
        component: USER_SEARCH_TAG_NAME,
        action: async () => {
            await import('./search/user/container');
        },
    },
    {
        path: RoutePath.FiUsers,
        component: FI_USERS_WRAPPER_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const canView = await (
                await getEntitlementsService()
            ).isEnabled(Entitlements.ViewFiUser);
            if (!canView) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./users/fi-users/fi-users-wrapper.component');
            return undefined;
        },
        children: [
            {
                path: RoutePath.FiUserRoles,
                component: FI_USER_ROLES_WRAPPER_TAG_NAME,
                action: async (context, commands) => {
                    const [canView, canAdd, canEdit] = await Promise.all([
                        (await getEntitlementsService()).isEnabled(Entitlements.ViewUserRole),
                        (await getEntitlementsService()).isEnabled(Entitlements.AddUserRole),
                        (await getEntitlementsService()).isEnabled(Entitlements.EditUserRole),
                    ]);
                    if (!canView && !canAdd && !canEdit) {
                        const institutionId = context.params.institutionId as string | undefined;
                        const prefix = institutionId ? `/${institutionId}` : '';
                        return commands.redirect(`${prefix}${RoutePath.FiUsers}`);
                    }
                    await import('./users/fi-users/roles/fi-user-roles.wrapper');
                    return undefined;
                },
                children: [
                    {
                        path: ':fiUserRoleId',
                        component: FI_USER_ROLE_TAG_NAME,
                        action: async () => {
                            await import('./users/fi-users/roles/fi-user-role.component');
                        },
                    },
                    {
                        path: '(.*)',
                        component: FI_USER_ROLES_TAG_NAME,
                        action: async () => {
                            await import('./users/fi-users/roles/fi-user-roles.component');
                        },
                    },
                ],
            },
            {
                path: RoutePath.FiUserProfile,
                component: FI_USER_PROFILE_TAG_NAME,
                action: async () => {
                    await import('./users/fi-users/fi-user-profile/fi-user-profile');
                },
            },
            {
                path: '(.*)',
                component: FI_USER_LIST_TAG_NAME,

                action: async () => {
                    await import('./users/fi-users/fi-users-list/fi-users-list');
                },
            },
        ],
    },
    {
        path: 'fi/product-configuration',
        component: 'fi-product-configuration-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewProductFeatureConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditProductFeatureConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./fi-product-configuration/container');
            return undefined;
        },
    },
    {
        path: 'fi/billing-configuration',
        component: 'fi-billing-configuration-container',
        action: async () => {
            await import('./fi-billing-configuration/container');
        },
    },
    {
        path: 'fi/company-user-settings/:tab',
        component: 'company-user-settings-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewCompanyUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.ManageCompanyUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./company-user-settings/container');
            return undefined;
        },
    },
    {
        path: 'fi/company-user-settings',
        component: 'company-user-settings-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewCompanyUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.ManageCompanyUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./company-user-settings/container');
            return undefined;
        },
    },
    {
        path: 'fi/fi-user-settings',
        component: 'fi-user-settings-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewFiUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.ManageFiUserSettings),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./fi-user-settings/container');
            return undefined;
        },
    },
    {
        path: 'fi/customer-engagement/information-center/:messageId',
        component: 'customer-engagement-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [
                canViewInfoCenter,
                canEditInfoCenter,
                canAddMessage,
                canViewResources,
                canEditResources,
                canAddLink,
                canSwitchFi,
            ] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.InformationCenterAddMessage
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewResourcePanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditResourcesPanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.ResourcesPanelAddLink),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (
                !canViewInfoCenter &&
                !canEditInfoCenter &&
                !canAddMessage &&
                !canViewResources &&
                !canEditResources &&
                !canAddLink &&
                !canSwitchFi
            ) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./customer-engagement/container');
            return undefined;
        },
    },
    {
        path: 'fi/customer-engagement/:tab',
        component: 'customer-engagement-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [
                canViewInfoCenter,
                canEditInfoCenter,
                canAddMessage,
                canViewResources,
                canEditResources,
                canAddLink,
                canSwitchFi,
            ] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.InformationCenterAddMessage
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewResourcePanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditResourcesPanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.ResourcesPanelAddLink),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (
                !canViewInfoCenter &&
                !canEditInfoCenter &&
                !canAddMessage &&
                !canViewResources &&
                !canEditResources &&
                !canAddLink &&
                !canSwitchFi
            ) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./customer-engagement/container');
            return undefined;
        },
    },
    {
        path: 'fi/customer-engagement',
        component: 'customer-engagement-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [
                canViewInfoCenter,
                canEditInfoCenter,
                canAddMessage,
                canViewResources,
                canEditResources,
                canAddLink,
                canSwitchFi,
            ] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditInformationCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.InformationCenterAddMessage
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewResourcePanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditResourcesPanelConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.ResourcesPanelAddLink),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (
                !canViewInfoCenter &&
                !canEditInfoCenter &&
                !canAddMessage &&
                !canViewResources &&
                !canEditResources &&
                !canAddLink &&
                !canSwitchFi
            ) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./customer-engagement/container');
            return undefined;
        },
    },
    {
        path: 'fi/processing-configuration/:tab',
        component: 'processing-configuration-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [
                canView,
                canEditCutoff,
                canAddHoliday,
                canEditHoliday,
                canDeleteHoliday,
                canSwitchFi,
            ] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewProcessingConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationEditCutoff
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsAdd
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsEdit
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsDelete
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (
                !canView &&
                !canEditCutoff &&
                !canAddHoliday &&
                !canEditHoliday &&
                !canDeleteHoliday &&
                !canSwitchFi
            ) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./processing-configuration/container');
            return undefined;
        },
    },
    {
        path: 'fi/processing-configuration',
        component: 'processing-configuration-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [
                canView,
                canEditCutoff,
                canAddHoliday,
                canEditHoliday,
                canDeleteHoliday,
                canSwitchFi,
            ] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewProcessingConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationEditCutoff
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsAdd
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsEdit
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ProcessingConfigurationHolidayExceptionsDelete
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (
                !canView &&
                !canEditCutoff &&
                !canAddHoliday &&
                !canEditHoliday &&
                !canDeleteHoliday &&
                !canSwitchFi
            ) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./processing-configuration/container');
            return undefined;
        },
    },
    {
        path: 'tc-configuration',
        component: 'transaction-code-configuration-container',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewTransactionCodeConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ManageTransactionCodeConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./transaction-code-configuration/container');
            return undefined;
        },
    },
    {
        path: 'terms-conditions-configuration',
        component: TERMS_CONDITIONS_CONFIGURATION_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewTermsAndConditions),
                (await getEntitlementsService()).isEnabled(Entitlements.ManageTermsAndConditions),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./terms-conditions-configuration/container');
            return undefined;
        },
    },
    {
        path: 'authentication-profile/:profileId',
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canAdd, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.AddChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.EditChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canAdd && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./authentication-profile/detail');
            const element = commands.component(AUTHENTICATION_PROFILE_DETAIL_TAG_NAME);
            (element as unknown as Element & { profileId: number }).profileId = Number(
                context.params.profileId
            );
            return element;
        },
    },
    {
        path: 'authentication-profile',
        component: AUTHENTICATION_PROFILE_LIST_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canAdd, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(Entitlements.ViewChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.AddChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.EditChallengePointProfile),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canAdd && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./authentication-profile/list');
            return undefined;
        },
    },
    {
        path: 'notification-configuration',
        component: NOTIFICATION_CONFIGURATION_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewNotificationConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.EditNotificationConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./notification-configuration/container');
            return undefined;
        },
    },
    {
        path: 'message-center-configuration',
        component: MESSAGE_CENTER_CONFIGURATION_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canManage, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewMessageCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ManageMessageCenterConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canManage && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./message-center-configuration/container');
            return undefined;
        },
    },
    {
        path: 'help',
        component: HELP_CONTAINER_TAG_NAME,
        action: async () => {
            await import('./help/container');
        },
    },
    {
        path: 'fi/content-management-configuration/:tab/:subTab',
        component: CONTENT_MANAGEMENT_CONFIGURATION_CONTAINER_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewContentManagementConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.EditContentManagement),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./content-management-configuration/container');
            return undefined;
        },
    },
    {
        path: 'fi/content-management-configuration/:tab',
        component: CONTENT_MANAGEMENT_CONFIGURATION_CONTAINER_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewContentManagementConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.EditContentManagement),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./content-management-configuration/container');
            return undefined;
        },
    },
    {
        path: 'fi/content-management-configuration',
        component: CONTENT_MANAGEMENT_CONFIGURATION_CONTAINER_TAG_NAME,
        action: async (context, commands) => {
            const institutionId = context.params.institutionId as string | undefined;
            const prefix = institutionId ? `/${institutionId}` : '';
            const [canView, canEdit, canSwitchFi] = await Promise.all([
                (await getEntitlementsService()).isEnabled(
                    Entitlements.ViewContentManagementConfiguration
                ),
                (await getEntitlementsService()).isEnabled(Entitlements.EditContentManagement),
                (await getEntitlementsService()).isEnabled(Entitlements.SwitchFi),
            ]);
            if (!canView && !canEdit && !canSwitchFi) {
                return commands.redirect(`${prefix}${RoutePath.Dashboard}`);
            }
            await import('./content-management-configuration/container');
            return undefined;
        },
    },
    {
        path: 'fi/product-configuration/:productFeatureId/reporting-configuration',
        component: 'fi-reporting-configuration',
        action: async () => {
            await import('./fi-product-configuration/partials/reporting-configuration/container');
        },
    },
    {
        path: 'account-reconciliation-reports-configuration',
        component: 'fi-account-reconciliation-reports-configuration',
        action: async () => {
            await import('./fi-product-configuration/partials/account-reconciliation-reports-configuration/container');
        },
    },
    {
        path: 'fi/product-configuration/:productFeatureId/positivepay-configuration',
        component: 'fi-positivepay-configuration',
        action: async () => {
            await import('./fi-product-configuration/partials/positivepay-configuration-fi/container');
        },
    },
    {
        path: 'fi/product-configuration/:productFeatureId/ach-configuration',
        component: 'fi-ach-configuration',
        action: async () => {
            await import('./fi-product-configuration/partials/ach-configuration/container');
        },
    },
];

export let routes: Route[] = [];

const { config } = window as unknown as InjectedWindow;
const bannoAuthEnabled = config?.bannoAuthEnabled?.toString() === 'true';

/**
 * Checks auth and executes path redirection
 * @param context
 * @param commands
 * @param unauthorizedPath
 * @param authorizedPath
 * @returns the correct route path based on auth result status
 */
const handleAuthAndRootPathRedirect = async (
    context: Context,
    commands: Commands,
    unauthorizedPath: () => RoutePath,
    authorizedPath: () => Promise<RoutePath> | RoutePath
) => {
    return (
        (await isAuthorized(context, commands, unauthorizedPath())) ??
        handleRootPathRedirect(context, commands, await authorizedPath())
    );
};
const appInitRoute = () => {
    // import company layout after auth due to message center requests
    import('./components/layouts/layout/tm-bo-layout.component');
    return RoutePath.Dashboard;
};

// Use institution specific routes if banno auth is enabled
if (bannoAuthEnabled) {
    routes = [
        {
            path: '/:institutionId([0-9a-fA-F-]{36})',
            component: LAYOUT_TAG_NAME,
            action: async (context, commands) => {
                // check auth, reroute to login if unauthorized
                return handleAuthAndRootPathRedirect(
                    context,
                    commands,
                    () => RoutePath.Login,
                    appInitRoute
                );
            },
            children: routeRouteChildRoutes,
        },
        {
            path: RoutePath.UserVerificationReset,
            component: RESET_PASSWORD_TAG_NAME,
            action: async () => {
                await import('./reset-password/container');
            },
        },
        {
            path: RoutePath.XperienceLogin,
            component: XPERIENCE_LOGIN_TAG_NAME,
            action: async () => {
                const [, { loadXperienceVendorScripts }] = await Promise.all([
                    import('./login/xperience-login/container'),
                    import('./xperience/load-vendor-scripts'),
                ]);
                loadXperienceVendorScripts();
            },
        },
    ];
} else {
    // Use the legacy routes if not and add the login route
    routes = [
        {
            path: RoutePath.UserVerificationReset,
            component: RESET_PASSWORD_TAG_NAME,
            action: async () => {
                await import('./reset-password/container');
            },
        },
        {
            path: RoutePath.Login,
            component: LOGIN_TAG_NAME,
            action: async () => {
                await import('./login/container');
            },
        },
        {
            path: RoutePath.XperienceLogin,
            component: XPERIENCE_LOGIN_TAG_NAME,
            action: async () => {
                const [, { loadXperienceVendorScripts }] = await Promise.all([
                    import('./login/xperience-login/container'),
                    import('./xperience/load-vendor-scripts'),
                ]);
                loadXperienceVendorScripts();
            },
        },
        {
            path: RoutePath.NewUserRegistration,
            component: NEW_USER_REGISTRATION_TAG_NAME,
            action: async () => {
                await import('./new-user-registration/container');
            },
        },
        {
            path: '/',
            component: LAYOUT_TAG_NAME,
            action: async (context, commands) => {
                // Allow unauthenticated access to the forgot-password page
                if (context.pathname.endsWith(RoutePath.ForgotPassword)) {
                    return undefined;
                }
                // If banno auth is not enabled use the legacy workflow and call from the root path
                // check auth, reroute to login if unauthorized
                return handleAuthAndRootPathRedirect(
                    context,
                    commands,
                    () => RoutePath.Login,
                    appInitRoute
                );
            },
            children: routeRouteChildRoutes,
        },
        {
            path: RoutePath.ForgotPassword,
            component: TM_BO_FORGOT_PASSWORD_TAG_NAME,
            action: async () => {
                await import('./forgot-password/container');
            },
        },
    ];
}
